import type { ReactNode } from 'react'

/**
 * Nothing here, said properly (M23-T03). Drawn on the medication frame
 * `1184:7830`: an illustration, *"No Pharmacy has responded yet"*, and
 * *"Answers usually land within an hour"*.
 *
 * **The second line is the component's whole point.** An empty admin queue is
 * ambiguous — it can mean *nothing has arrived*, *your filter excludes
 * everything*, or *the work is done*. Those need different words, and on
 * Emergency and Leads an empty list is **good news** and should read that way.
 * So `children` is not optional decoration; a heading alone is a worse empty
 * state than none.
 *
 * The illustration is optional on purpose: M17's rule is that a missing asset
 * is a reason to ship text-first, not to invent one.
 */
export function EmptyState({
  title,
  children,
  illustration,
  action,
}: {
  title: string
  children: ReactNode
  illustration?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      {illustration !== undefined && (
        <div aria-hidden="true" className="mb-2">
          {illustration}
        </div>
      )}
      <p className="font-display text-lg font-semibold text-ink-1">{title}</p>
      <p className="max-w-md text-sm text-ink-3">{children}</p>
      {action !== undefined && <div className="mt-3">{action}</div>}
    </div>
  )
}
